import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import CIcon from '@coreui/icons-react'
import { cilLayers } from '@coreui/icons'
import { CCard, CCardHeader, CCardBody } from '@coreui/react'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { usePendiente } from '../components/ww/pendiente'

const columnasBase: WwColumn[] = [
  { key: 'cajaNro', title: 'Nro', type: 'number' },
  { key: 'cajaNombre', title: 'Nombre' },
  { key: 'cajaIngresosTotal', title: 'Ingresos Capital', type: 'currency' },
  { key: 'cajaIngresosAbonos', title: 'Abonos Prestamo', type: 'currency' },
  { key: 'cajaIngresosCondenas', title: 'Ingresos Condenas', type: 'currency' },
  { key: 'cajaPrestamosTotal', title: 'Prestamos Total', type: 'currency' },
  { key: '_totalOperativos', title: 'Total Operativos', type: 'currency' },
  { key: '_totalDisponible', title: 'Total Disponible', type: 'currency' },
]

// Columnas de factura (Abonos Factura / Facturas Contado — caja.cs réplica):
// SOLO si la empresa tiene el módulo de facturación activo
// (EmpresaModuloFacturacion — MenuController modfactura).
const columnasFactura: WwColumn[] = [
  { key: '_abonosFactura', title: 'Abonos Factura', type: 'currency' },
  { key: '_facturasContado', title: 'Facturas Contado', type: 'currency' },
]

// Cajas — réplica de cajageneral.cs:55 (cajageneral GX).
const CajasPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // Módulo de facturación: columnas Abonos Factura / Facturas Contado
  // solo si está activo (EmpresaModuloFacturacion — MenuController modfactura).
  const [modFacturacion, setModFacturacion] = useState(false)
  useEffect(() => {
    api.get<any>('/cuenta?empresa=' + empresa)
      .then((cuenta) => setModFacturacion(Number(cuenta.moduloFacturacion ?? 0) === 1
        // [RGM] 2026-10-07 (pedido Rafael: "las columnas de caja con los datos
        // de factura"): también aplican en motor SOLO FACTURACIÓN.
        || String(cuenta.modo ?? '') === 'FACTURACION'))
      .catch(() => undefined)
  }, [empresa])
  // ── Filtros rápidos (WwFilterBar) ──
  const [filtroCaja, setFiltroCaja] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Fila en edición (cajaww.cs:2431 'DO UPDATE' → webobject caja Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [errorModal, setErrorModal] = useState('')
  const [form, setForm] = useState({
    cajaNombre: '',
    libreValor1: 0, libreValor2: 0, libreValor3: 0,
    libreNumerico1: 0, libreNumerico2: 0, libreCaracter1: '',
  })

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroCaja && String(row.cajaNombre ?? '').trim() !== filtroCaja) return false
    return true
  }), [rows, filtroCaja])
  const cajas = useMemo(() => [...new Set(rows.map((r) => String(r.cajaNombre ?? '').trim()).filter(Boolean))], [rows])
  const chips = [filtroCaja ? { clave: 'caja', label: `Caja: ${filtroCaja}` } : null].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => setFiltroCaja('')
  const quitarFiltro = (clave: string) => { if (clave === 'caja') setFiltroCaja('') }
  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/cajas/${empresa}/resumen`)
      .then((d) =>
        setRows(d.map((c) => ({
          ...c,
          // Mapeo de los campos reales de la tabla (el GX los expone:
          // CajaIngresosAbonosFactura, CajaIngresosFacturasContado) y de
          // los atributos fórmula del backend (_totalDisponible =
          // CajaTotal caja.cs:1302; _totalOperativos = CajaEgresosTotal
          // con el label GX caja.cs:479).
          _abonosFactura: c.cajaIngresosAbonosFactura ?? 0,
          _facturasContado: c.cajaIngresosFacturasContado ?? 0,
        }))),
      )
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  // cajaLIbreValor3/cajaLIbreNumerico1-2: nombre FISICO real del GX (typo
  // preservado — caja.cs:566 edtCajaLIbreValor3; igual que ZonaLIbreNumerico1).
  // NO renombrar (contrato BD/EF).
  const abrirNuevo = () => {
    setEditando(null)
    setForm({
      cajaNombre: '',
      libreValor1: 0, libreValor2: 0, libreValor3: 0,
      libreNumerico1: 0, libreNumerico2: 0, libreCaracter1: '',
    })
    setAbierto(true)
  }

  // Modificar (cajaww.cs:2431): carga la fila en el form y abre el modal.
  // Solo campos editables del webobject (caja.cs:2529 CajaNombre, 2481-2497
  // libres); los totales se acumulan y NO se editan (caja.cs:2513-2527).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      cajaNombre: fila.cajaNombre ?? '',
      libreValor1: Number(fila.cajaLibreValor1 ?? 0),
      libreValor2: Number(fila.cajaLibreValor2 ?? 0),
      libreValor3: Number(fila.cajaLIbreValor3 ?? 0),
      libreNumerico1: Number(fila.cajaLIbreNumerico1 ?? 0),
      libreNumerico2: Number(fila.cajaLIbreNumerico2 ?? 0),
      libreCaracter1: fila.cajaLibreCaracter1 ?? '',
    })
    setAbierto(true)
  }

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    setGuardando(true)
    try {
      if (editando) {
        // cajaww.cs:2431 — DO UPDATE: webobject caja Mode="UPD" con CajaNro
        // (la llave viaja en la URL, no cambia). El backend replica
        // BC00068 (caja_bc.cs:1799) solo con campos editables.
        await api.put(`/cajas/${editando.cajaNro}`, {
          empresaCodigo: empresa,
          cajaNombre: form.cajaNombre,
          libreValor1: Number(form.libreValor1) || 0,
          libreValor2: Number(form.libreValor2) || 0,
          libreValor3: Number(form.libreValor3) || 0,
          libreNumerico1: Number(form.libreNumerico1) || 0,
          libreNumerico2: Number(form.libreNumerico2) || 0,
          libreCaracter1: form.libreCaracter1 || undefined,
        })
      } else {
        await api.post('/cajas', {
          empresaCodigo: empresa,
          cajaNombre: form.cajaNombre,
          libreValor1: Number(form.libreValor1) || 0,
          libreValor2: Number(form.libreValor2) || 0,
          libreValor3: Number(form.libreValor3) || 0,
          libreNumerico1: Number(form.libreNumerico1) || 0,
          libreNumerico2: Number(form.libreNumerico2) || 0,
          libreCaracter1: form.libreCaracter1 || undefined,
        })
      }
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminar = (fila: any) => {
    // cajaww.cs:2439 — DO DELETE: webobject caja Mode="DLT" con CajaNro.
    // El backend replica OnDeleteControls065 (caja_bc.cs:540-570):
    // Zona/Ingresos → 409, sin refs → DELETE (BC00069, caja_bc.cs:1800).
    if (!window.confirm(`¿Eliminar la caja ${fila.cajaNombre || fila.cajaNro}?`)) return
    api.delete(`/cajas/${fila.cajaNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Caja eliminada.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Caja</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}
      <WwGrid
        filters={
          <WwFilterBar
            selects={[
              { key: 'caja', label: 'Todas las Cajas', opciones: cajas, valor: filtroCaja, onChange: setFiltroCaja },
            ]}
            chips={chips}
            onQuitar={quitarFiltro}
            onLimpiar={limpiarFiltros}
          />
        }
        title="Caja"
        columns={modFacturacion ? [...columnasBase, ...columnasFactura] : columnasBase}
        rows={visibleRows}
        busquedaPlaceholder="Buscar por caja..." 
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirNuevo}
        onEditar={abrirEditar}
        onEliminar={eliminar}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar caja' : 'Nueva caja'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            {/* Tarjeta única (patrón formularios nuevos): los campos libres de
                la caja NO se usan en el form del GX (cajaww — sin captions de
                negocio) → ocultos (decisión Rafael 2026-08-27). */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilLayers} size="sm" /> Datos de la Caja
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  {editando && (
                    <CCol md={4}><CFormLabel>Nro</CFormLabel><CFormInput value={editando.cajaNro} disabled /></CCol>
                  )}
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CCol md={editando ? 8 : 12}><CFormLabel>Nombre</CFormLabel><CFormInput value={form.cajaNombre} onChange={(e) => setForm({ ...form, cajaNombre: e.target.value.toUpperCase() })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear caja'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default CajasPage
